
import { Task } from "../../types/tasks";
import { Card } from "@/components/ui/card";
import TaskCard from "./TaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface TaskListProps {
  tasks: Task[];
  onStatusChange: (id: string, completed: boolean) => void;
  onView: (id: string) => void;
  isAdminView: boolean;
  currentUserId?: string;
}

const TaskList = ({
  tasks,
  onStatusChange,
  onView,
  isAdminView,
  currentUserId,
}: TaskListProps) => {
  const pendingTasks = tasks.filter(task => task.status !== "completed");
  const completedTasks = tasks.filter(task => task.status === "completed");
  const overdueTasks = tasks.filter(task => task.status === "overdue");
  const hasOverdueTasks = overdueTasks.length > 0;

  return (
    <Tabs defaultValue="active" className="w-full">
      <TabsList>
        <TabsTrigger value="active">
          Active ({pendingTasks.length})
        </TabsTrigger>
        <TabsTrigger value="completed">
          Completed ({completedTasks.length})
        </TabsTrigger>
        <TabsTrigger 
          value="overdue" 
          className={cn(
            hasOverdueTasks ? "text-red-600 font-medium" : ""
          )}
        >
          Overdue ({overdueTasks.length})
        </TabsTrigger>
        <TabsTrigger value="all">
          All ({tasks.length})
        </TabsTrigger>
      </TabsList>
      <div className="mt-4">
        <TabsContent value="active" className="m-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pendingTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStatusChange={onStatusChange}
                onView={onView}
                isAdminView={isAdminView}
                currentUserId={currentUserId}
              />
            ))}
            {pendingTasks.length === 0 && (
              <Card className="col-span-full p-6 text-center text-muted-foreground">
                No active tasks match your filters.
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="completed" className="m-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {completedTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStatusChange={onStatusChange}
                onView={onView}
                isAdminView={isAdminView}
                currentUserId={currentUserId}
              />
            ))}
            {completedTasks.length === 0 && (
              <Card className="col-span-full p-6 text-center text-muted-foreground">
                No completed tasks match your filters.
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="overdue" className="m-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {overdueTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStatusChange={onStatusChange}
                onView={onView}
                isAdminView={isAdminView}
                currentUserId={currentUserId}
              />
            ))}
            {overdueTasks.length === 0 && (
              <Card className="col-span-full p-6 text-center text-muted-foreground">
                No overdue tasks match your filters.
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="all" className="m-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStatusChange={onStatusChange}
                onView={onView}
                isAdminView={isAdminView}
                currentUserId={currentUserId}
              />
            ))}
            {tasks.length === 0 && (
              <Card className="col-span-full p-6 text-center text-muted-foreground">
                No tasks match your filters.
              </Card>
            )}
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default TaskList;
